import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "@/hooks/use-toast";
import { api } from "@/lib/api";
import { TestTube } from "lucide-react";
import { useEffect, useState } from "react";

interface Exame {
  id: number;
  nome: string;
  descricao?: string;
  ativo: boolean;
}

interface ExamesSolicitacaoProps {
  initialExams?: string[];
  availableExams?: string[];
  onExamsChange?: (exams: string[]) => void;
  protocoloId?: number;
  onSaveExames?: (exames: string[]) => Promise<void>;
}

const ExamesSolicitacao = ({ 
  initialExams = [], 
  availableExams: propAvailableExams,
  onExamsChange,
  protocoloId,
  onSaveExames
}: ExamesSolicitacaoProps) => {
  const [selectedExams, setSelectedExams] = useState<string[]>(initialExams);
  const [tempSelectedExams, setTempSelectedExams] = useState<string[]>(initialExams);
  const [isExamDialogOpen, setIsExamDialogOpen] = useState(false);
  const [availableExams, setAvailableExams] = useState<string[]>(propAvailableExams || []);
  const [loadingExams, setLoadingExams] = useState(false);
  const [savingExames, setSavingExames] = useState(false);


  // Atualizar selectedExams quando initialExams mudar
  useEffect(() => {
    setSelectedExams(initialExams);
    setTempSelectedExams(initialExams);
  }, [initialExams]);

  // Atualizar availableExams quando propAvailableExams mudar
  useEffect(() => {
    if (propAvailableExams) {
      setAvailableExams(propAvailableExams);
    }
  }, [propAvailableExams]);


  // Buscar exames do backend quando o modal for aberto
  useEffect(() => {
    const fetchExames = async () => {
      // Se availableExams já foi fornecido via props, não busca do backend
      if (propAvailableExams && propAvailableExams.length > 0) {
        return;
      }

      if (isExamDialogOpen) {
        setLoadingExams(true);
        try {
          const response: { result: Exame[] } = await api.get("/api/exames");
          const examesAtivos = response.result
            ?.filter((exame) => exame.ativo)
            .map((exame) => exame.nome) || [];
          
          setAvailableExams(examesAtivos);
        } catch (error) {
          console.error("Erro ao buscar exames:", error);
          toast({
            title: "Erro",
            description: "Não foi possível carregar os exames cadastrados.",
            variant: "destructive",
          });
          setAvailableExams([]);
        } finally {
          setLoadingExams(false);
        }
      }
    };

    fetchExames();
  }, [isExamDialogOpen, propAvailableExams]);

  const handleExamSelection = (newExams: string[]) => {
    setSelectedExams(newExams);
    onExamsChange?.(newExams);
  };

  const handleRemoveExam = async (exam: string) => {
    // Guardar o estado anterior para reverter em caso de erro
    const previousExams = [...selectedExams];
    const updated = selectedExams.filter((e) => e !== exam);
    
    // Atualizar estado local primeiro
    handleExamSelection(updated);
    
    // Se houver protocoloId, atualizar no banco de dados
    if (protocoloId && onSaveExames) {
      try {
        setSavingExames(true);
        console.log('🗑️ [ExamesSolicitacao] Removendo exame do banco:', {
          exame: exam,
          protocoloId,
          examesRestantes: updated
        });
        
        await onSaveExames(updated);
        
        toast({
          title: "Exame removido!",
          description: `O exame "${exam}" foi removido com sucesso.`,
        });
      } catch (error) {
        console.error('❌ [ExamesSolicitacao] Erro ao remover exame:', error);
        toast({
          title: "Erro",
          description: "Não foi possível remover o exame. Tente novamente.",
          variant: "destructive"
        });
        // Reverter a remoção em caso de erro
        handleExamSelection(previousExams);
      } finally {
        setSavingExames(false);
      }
    }
  };

  const handleConfirmExams = async () => {
    if (tempSelectedExams.length === 0) {
      toast({
        title: "Atenção",
        description: "Selecione pelo menos um exame.",
        variant: "destructive"
      });
      return;
    }

    // Atualizar a lista de exames selecionados
    handleExamSelection(tempSelectedExams);

    // Salvar exames via API se protocoloId estiver disponível
    if (protocoloId && onSaveExames) {
      setSavingExames(true);
      try {
        await onSaveExames(tempSelectedExams);
        toast({
          title: "Exames incluídos com sucesso!",
          description: `${tempSelectedExams.length} exame(s) adicionados e salvos.`,
        });
        setIsExamDialogOpen(false);
      } catch (error) {
        console.error('Erro ao salvar exames:', error);
        toast({
          title: "Erro",
          description: "Não foi possível salvar os exames. Tente novamente.",
          variant: "destructive"
        });
      } finally {
        setSavingExames(false);
      }
    } else {
      // Se não tiver protocoloId, apenas atualizar a lista
      toast({
        title: "Exames incluídos!",
        description: `${tempSelectedExams.length} exame(s) adicionados. Salve o formulário para salvar os exames.`,
      });
      setIsExamDialogOpen(false);
    }
  };


  const hasExams = selectedExams.length > 0;

  return (
    <>
      <Card className="p-6">
        <div className="space-y-4">
          {/* Lista de exames selecionados */}
          {hasExams ? (
            <div className="space-y-3">
              <h4 className="text-sm font-semibold">Exames Solicitados:</h4>
              <ul className="space-y-1">
                {selectedExams.map((exam, i) => (
                  <li key={i} className="flex items-center justify-between border rounded-md p-2 text-sm">
                    <span>{exam}</span>
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => handleRemoveExam(exam)}
                    >
                      Remover
                    </Button>
                  </li>
                ))}
              </ul>
            </div>
          ) : (
            <p className="text-sm text-muted-foreground">Nenhum exame solicitado ainda.</p>
          )}

          {/* Botões principais */}
          <div className="flex gap-2">
            <Button
              variant="outline"
              className="flex-1"
              onClick={() => {
                setTempSelectedExams(selectedExams);
                setIsExamDialogOpen(true);
              }}
            >
              <TestTube className="h-4 w-4 mr-2" />
              Solicitar Exames
            </Button>
          </div>
        </div>
      </Card>

      {/* Modal de Seleção de Exames */}
      <Dialog open={isExamDialogOpen} onOpenChange={setIsExamDialogOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Selecionar Exames</DialogTitle>
          </DialogHeader>
          {loadingExams ? (
            <div className="flex items-center justify-center py-8">
              <p className="text-sm text-muted-foreground">Carregando exames...</p>
            </div>
          ) : availableExams.length === 0 ? (
            <div className="flex items-center justify-center py-8">
              <p className="text-sm text-muted-foreground">Nenhum exame cadastrado.</p>
            </div>
          ) : (
            <div className="space-y-3 max-h-[60vh] overflow-y-auto">
              {availableExams.map((exam) => (
                <label
                  key={exam}
                  className="flex items-center gap-2 p-2 border rounded-md cursor-pointer hover:bg-accent/40 transition-colors"
                >
                  <input
                    type="checkbox"
                    checked={tempSelectedExams.includes(exam)}
                    onChange={(e) => {
                      if (e.target.checked)
                        setTempSelectedExams((prev) => [...prev, exam]);
                      else
                        setTempSelectedExams((prev) => prev.filter((x) => x !== exam));
                    }}
                    className="w-4 h-4 accent-primary"
                  />
                  <span className="text-sm">{exam}</span>
                </label>
              ))}
            </div>
          )}

          <div className="flex justify-end gap-2 mt-4">
            <Button variant="outline" onClick={() => setIsExamDialogOpen(false)}>
              Cancelar
            </Button>
            <Button 
              onClick={handleConfirmExams} 
              disabled={loadingExams || savingExames || tempSelectedExams.length === 0}
            >
              {savingExames ? 'Salvando...' : 'Incluir Exames'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default ExamesSolicitacao;

